import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { fetchZmianyLogow } from '@/lib/logi-sps-api';

/**
 * ZMIANY W PLIKACH LOGÓW SPS — zakładka w „Logi i statystyki".
 *
 * ⚠️ TO JEDYNE MIEJSCE W PORTALU, KTÓRE WIDZI AWARIĘ SAMEGO SUPPLYPOINTA. Agent pilnuje BAZY;
 * gdy program się wywala, baza jest zdrowa i portal nie ma o czym mówić. Ślad zostaje na maszynie —
 * w `FatalErrors.log` i w znacznikach `AppInfo\APPCRASH_SPSUI_*` — i to on trafia tutaj.
 *
 * ⚠️ PUSTA ZAKŁADKA JEST DOBRĄ WIADOMOŚCIĄ i tak jest opisana. Większości tych plików na maszynie
 * nie ma, bo powstają dopiero po włączeniu debugowania w SPS; brak wpisów znaczy „nic się nie
 * wywaliło i nikt nie włączył debugowania", a nie „dozór nie działa".
 */

const OKNA = [1, 7, 30, 90] as const;

const ROZMIAR = (b: number | null): string => {
  if (b === null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export function LogiSps() {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const [dni, setDni] = useState<number>(7);
  const [klucz, setKlucz] = useState<string>('');
  const [rozwiniete, setRozwiniete] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['logi-sps', groupId, dni, klucz],
    queryFn: () => fetchZmianyLogow(groupId!, dni, klucz === '' ? undefined : klucz),
    enabled: Boolean(groupId),
  });

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('spsLog.pickIsland')}</p>;
  if (isLoading || data === undefined) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;

  const przelacz = (id: string) => {
    setRozwiniete((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {OKNA.filter((d) => d <= data.dniPrzechowywania).map((d) => (
          <Button key={d} size="sm" variant={dni === d ? 'default' : 'outline'} onClick={() => setDni(d)}>
            {d === 1 ? t('spsLog.day') : `${d} ${t('spsLog.days')}`}
          </Button>
        ))}
        {/* Filtr po pliku pokazujemy TYLKO dla plików, które faktycznie coś zgłosiły. */}
        {data.poPliku.length > 1 && (
          <>
            <Button size="sm" variant={klucz === '' ? 'default' : 'outline'} onClick={() => setKlucz('')}>
              {t('spsLog.allFiles')}
            </Button>
            {data.poPliku.map((p) => (
              <Button
                key={p.klucz}
                size="sm"
                variant={klucz === p.klucz ? 'default' : 'outline'}
                onClick={() => setKlucz(p.klucz)}
              >
                {p.plik}
                <Badge variant="neutral">{p.ile}</Badge>
              </Button>
            ))}
          </>
        )}
      </div>

      {data.zmiany.length === 0 ? (
        <p className="max-w-3xl text-sm text-muted-foreground">{t('spsLog.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-2">{t('spsLog.when')}</th>
                <th className="p-2">{t('spsLog.node')}</th>
                <th className="p-2">{t('spsLog.file')}</th>
                <th className="p-2">{t('spsLog.what')}</th>
                <th className="p-2 text-right">{t('spsLog.size')}</th>
              </tr>
            </thead>
            <tbody>
              {data.zmiany.map((z) => (
                <tr key={z.id} className="border-t align-top">
                  <td className="p-2 whitespace-nowrap text-[12px]">
                    {new Date(z.at).toLocaleString('pl')}
                    {/*
                      ⚠️ CZAS PLIKU OBOK CZASU WYKRYCIA, gdy się różnią. Zmianę wykrywamy w następnym
                      cyklu agenta, więc „wykryto 12:03" przy wpisie z 11:47 nie jest sprzecznością —
                      ale bez obu liczb wygląda na jedną.
                    */}
                    {z.mtime !== null && Math.abs(new Date(z.mtime).getTime() - new Date(z.at).getTime()) > 90_000 && (
                      <>
                        <br />
                        <span className="text-[11px] text-muted-foreground">
                          {t('spsLog.fileTime')} {new Date(z.mtime).toLocaleString('pl')}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="p-2 text-[12px]">{z.nodeId}</td>
                  <td className="p-2">
                    <span className="font-mono text-[12px]">{z.plik}</span>
                    <br />
                    <span className="text-[11px] text-muted-foreground">{z.sciezka}</span>
                  </td>
                  <td className="p-2">
                    <Badge variant={z.rodzaj === 'ZNIKL' ? 'neutral' : 'warning'}>
                      {t(`spsLog.kind.${z.rodzaj}`)}
                    </Badge>
                    {z.fragment !== null && z.fragment !== '' && (
                      <div className="mt-1">
                        <button
                          type="button"
                          className="text-[11px] underline decoration-dotted"
                          onClick={() => przelacz(z.id)}
                        >
                          {rozwiniete.has(z.id) ? t('spsLog.hideContent') : t('spsLog.showContent')}
                        </button>
                        {rozwiniete.has(z.id) && (
                          <pre className="mt-1 max-h-64 max-w-[46rem] overflow-auto rounded border bg-muted/30 p-2 text-[11px] whitespace-pre-wrap">
                            {z.fragment}
                            {z.uciety ? `\n\n${t('spsLog.truncated')}` : ''}
                          </pre>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap text-[12px] tabular-nums">
                    {ROZMIAR(z.rozmiarPo)}
                    {z.rozmiarPrzed !== null && z.rozmiarPo !== null && z.rozmiarPo > z.rozmiarPrzed && (
                      <>
                        <br />
                        <span className="text-[11px] text-muted-foreground">
                          +{ROZMIAR(z.rozmiarPo - z.rozmiarPrzed)}
                        </span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">{t('spsLog.footnote')}</p>
    </div>
  );
}
